import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';
import { datetimeFormat } from 'app/constants/date-time-format';
import { DateService, HelperService, HTTPService } from 'app/services';
import { debounce } from 'lodash';

/**
 * Interface for warranty details
 */
interface Warranty {
    partName: string;
    partNumber: string;
    warrantyNumber: string;
    invoiceCode: string;
    invoiceDate: string | null;
    previousWarrantyNumber?: string;
    customer?: string;
}

@Component({
    selector: 'app-manual-enter-warranty',
    templateUrl: './manual-enter-warranty.component.html',
    styleUrls: ['./manual-enter-warranty.component.scss'],
})
export class ManualEntryWarrantyComponent implements OnInit {
    @Input() isWarrantyNumberAddonEnabled: boolean = false;
    @Input() warrantyEligible: any = {};
    @Input() error: any = {};
    @Input() data: any;
    @Input() styleClass: any;
    @Input() invoicedRO: boolean = false;
    @Input() openFrom: string = 'merge-prompt';

    @Output() warrantySave = new EventEmitter<any>();

    isLoading: boolean = false;
    isManuallyEntered: boolean = true;
    isWarrantySelected: boolean = false;
    searchQuery: string = '';
    suggestions: Warranty[] = [];
    focus = {
        search: false,
    }
    isDisabledPartNumber = false;
    /** Default warranty suggestions */
    defaultSuggestions: Warranty[] = [
        {
            warrantyNumber: '',
            partName: '',
            partNumber: '',
            invoiceDate: '',
            invoiceCode: '',
            customer: '',
        },
    ];

    /** Holds part warranty history */
    partWarrantiesHistory: Warranty[] = [this.getEmptyWarrantyObject()];

    /** Max date range for warranty selection */
    maxDateRange: Date = new Date();

    constructor(
        private dateService: DateService,
        private httpService: HTTPService,
        private helperService: HelperService
    ) { }

    ngOnInit(): void {
        this.initializeWarrantySelection();
    }

    /**
     * Initializes warranty selection based on input data.
     */
    private initializeWarrantySelection(): void {
        if (
            this.warrantyEligible?.replacementInvoice &&
            this.warrantyEligible?.replacementInvoice?.partNumber &&
             this.warrantyEligible.partWarrantyType === 'manually-entered') {
            this.warrantySelect(this.warrantyEligible.replacementInvoice, true);
        }
    }

    /**
     * Clears the warranty selection and resets the form.
     */
    clearWarrantySelection(): void {
        this.isWarrantySelected = false;
        this.isManuallyEntered = true;
        this.searchQuery = '';
        this.partWarrantiesHistory = [this.getEmptyWarrantyObject()];
        this.suggestions = null
        this.error = {};
        this.isDisabledPartNumber = false;
        // this.isLoading = false;

        this.warrantyEligible.selectedPart = {};
        this.warrantyEligible.warrantyNumber = '';
        this.warrantySave.emit(this.warrantyEligible);
    }

    /**
     * Handles changes to warranty fields.
     * @param key - The field to update.
     * @param value - The new value for the field.
     */
    handleWarrantyFieldChange(key: keyof Warranty, value: any): void {
        this.partWarrantiesHistory[0][key] = value;
        this.warrantyEligible.selectedPart = this.partWarrantiesHistory[0];

        if (key === 'warrantyNumber') {
            this.warrantyEligible.warrantyNumber = value;
        }

        this.warrantySave.emit(this.warrantyEligible);
    }

    /**
     * Returns an empty warranty object.
     * @returns An empty Warranty object.
     */
    private getEmptyWarrantyObject(): Warranty {
        return {
            partName: '',
            partNumber: '',
            warrantyNumber: '',
            invoiceCode: '',
            invoiceDate: null,
            previousWarrantyNumber: '',
        };
    }

    /**
     * Checks if the warranty section should be disabled.
     * @returns True if the warranty section should be disabled, otherwise false.
     */
    isWarrantySectionDisabled(): boolean {
        return this.invoicedRO || this.warrantyEligible?.partWarrantyType !== 'manually-entered';
    }

    /**
     * Handles warranty selection from the suggestions list.
     * @param warranty - The selected warranty.
     */
    warrantySelect(warranty: Warranty, resetSearchKey = false): void {
        this.isManuallyEntered = true;
        this.isWarrantySelected = true;

        if(!resetSearchKey){
            this.searchQuery = warranty.warrantyNumber;
        }

        this.partWarrantiesHistory = [
            {
                ...warranty,
                invoiceDate: warranty.invoiceDate ? this.dateService.formatDate(
                    new Date(warranty.invoiceDate),
                    datetimeFormat.yyymmdd
                ) : '',
            },
        ];

        this.warrantyEligible.selectedPart = this.partWarrantiesHistory[0];
        this.warrantyEligible.warrantyNumber = warranty.warrantyNumber;

        if(warranty.partNumber){
            this.isDisabledPartNumber = true;
        }
        
        this.warrantySave.emit(this.warrantyEligible);
    }

    /**
     * Searches for warranties by item ID.
     * @param query - The item ID to search for.
     */
    async searchWarrantiesByItemId(query: string): Promise<void> {
        this.isLoading = true;
        this.focus.search = false;
        this.searchQuery = query;
        const payload = { zCode: query };

        try {
            const warranties = await this.httpService.getWarrantiesSearchByZCode(payload);
            this.suggestions = warranties.map((warranty) => ({
                invoiceCode: warranty.invCode,
                partNumber: warranty.partNo,
                warrantyNumber: warranty.originalWarranty,
                partName: warranty.partName,
                invoiceDate: warranty.invoiceDate ? this.dateService.formatDate(
                    this.dateService.getCurrentDateTimeTz(new Date(warranty.invoiceDate).getTime()),
                    datetimeFormat.yyymmdd
                ) : '',
            }));
        } catch (error) {
            this.helperService.showToaster('error', error || 'An error occurred while fetching warranties.');
        } finally {
            this.isLoading = false;
        }
    }

    /**
     * Handles date change events.
     * @param date - The new date value.
     */
    onDateChange(date: any): void {
        this.handleWarrantyFieldChange('invoiceDate', date);
    }

    /**
     * Resets the form to its initial state.
     */
    resetForm(warrantyNumber = ''): void {
        this.focus.search = true;
        this.clearWarrantySelection();

        if (warrantyNumber) {
            this.focus.search = false;
            this.partWarrantiesHistory = [{ ...this.getEmptyWarrantyObject(), warrantyNumber }];
            this.warrantySave.emit(this.warrantyEligible);
        }
    }

    /**
     * Handles search query changes.
     * @param query - The new search query.
     */
    onSearchQueryChange = debounce((query: string) => {
        this.isManuallyEntered = false;

        if (query.length > 2) {
            this.searchWarrantiesByItemId(query);
        } else {
            this.searchQuery = query;
            this.isManuallyEntered = true;
            this.suggestions = null;
        }
    }, 600);
}